import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { translationAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { RouterPrompt } from 'components/RouterPrompt';
import { TEMPORARY } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { TRANSLATION_FILE_STATUS, UPLOAD_FILES_STATUS } from 'helpers/enum';
import { Languages, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Task } from 'types/reducers/task';
import { TranslationTask } from 'types/reducers/translation';

interface TaskListProps {
  onAddNew: () => void;
  onChange: (_task: Task) => void;
  isToggleSidebar: boolean;
  setIsToggleSidebar: React.Dispatch<React.SetStateAction<boolean>>;
  /** When rendered inside the mobile Drawer: fill height, hide the collapse toggle. */
  isInDrawer?: boolean;
}

const TranslationJobs = ({
  onAddNew,
  onChange,
  isToggleSidebar,
  setIsToggleSidebar,
  isInDrawer = false,
}: TaskListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tasks, current } = useSelector((state) => state.translation);
  const { status } = useSelector((state) => state.upload);
  const { data, total_page, current_page } = tasks;

  const [recordDelete, setRecordDelete] = useState<TranslationTask>();
  const [recordEdit, setRecordEdit] = useState<TranslationTask>();
  const [selectTask, setSelectTask] = useState<TranslationTask>();

  const {
    isOpen: isShowWarningChangeTaskDialog,
    onOpen: onOpenWarningChangeTaskDialog,
    onClose: onCloseWarningChangeTaskDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const selectedTaskId = current?.task_id;
  const selectedFiles = current?.selectedFiles;
  const isUploadingFiles = status === UPLOAD_FILES_STATUS.UPLOADING;
  const isWaitingUpdateFileUploadStatus = selectedFiles.some((file) =>
    [TRANSLATION_FILE_STATUS.UPLOADED, TRANSLATION_FILE_STATUS.IDLE].includes(
      file.translation_file_status
    )
  );
  const isChangingSelectedFiles =
    JSON.stringify(current?.selectedFiles) !== JSON.stringify(current?.initialSelectedFiles);

  const isBlockingAddNewTask =
    data?.[0]?.type === TEMPORARY || isWaitingUpdateFileUploadStatus || isUploadingFiles;

  const onShowMore = () => {
    dispatch(
      translationAction.getTranslationTasks({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (task: TranslationTask) => {
    setRecordDelete(task);
    onOpenDeleteDialog();
  };

  const onClickEdit = (task: TranslationTask) => {
    setRecordEdit(task);
    onOpenEditDialog();
  };

  const handleDelete = (task: TranslationTask) => {
    dispatch(
      translationAction.deleteTranslationTask({
        id: task.id,
      })
    );
  };

  const handleEdit = (updateTask: TranslationTask & { new_name: string }) => {
    dispatch(
      translationAction.editTranslationTaskName({
        id: updateTask.id,
        new_name: updateTask.new_name,
      })
    );
  };

  const onChangeTask = (task: Task) => {
    // Block change task when waiting file upload status updated
    if (isWaitingUpdateFileUploadStatus || isUploadingFiles) {
      return;
    }
    if (isChangingSelectedFiles && current.type !== TEMPORARY && current.task_id !== task.id) {
      onOpenWarningChangeTaskDialog();
      setSelectTask(task);
    } else {
      onContinueChangeTask(task);
    }
  };

  const onContinueChangeTask = (task: Task) => {
    onChange(task);
  };

  const onClickToggleSideBar = () => {
    setIsToggleSidebar(!isToggleSidebar);
  };

  useEffect(() => {
    if (selectedTaskId) {
      dispatch(translationAction.getDetailTranslationTask(current));
    }
  }, [selectedTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <RouterPrompt when={isChangingSelectedFiles} />
      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('translator.deleteTask')}
        dataBody={t('translator.deleteTaskInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('translator.renameTask')}
        btnAction={t('translator.rename')}
      />
      {/* DS jobs pane — surface, border, ds_sm shadow; collapsible (design-guidelines.md §4/§5) */}
      <Flex
        pos='relative'
        direction='column'
        overflow='hidden'
        w={isToggleSidebar ? '100%' : '52px'}
        h='100%'
        minH={0}
        bg='bg.surface'
        borderWidth={isInDrawer ? '0' : '1px'}
        borderColor='border.default'
        borderRadius={isInDrawer ? 'none' : 'lg'}
        shadow={isInDrawer ? 'none' : isToggleSidebar ? 'ds_sm' : 'none'}
        transition='width .12s ease-out'
      >
        {/* Header: title + new job + single collapse affordance */}
        <Flex
          align='center'
          direction={isToggleSidebar ? 'row' : 'column'}
          flexShrink={0}
          gap={2}
          px={isToggleSidebar ? 4 : 1}
          pt={4}
          pb={isToggleSidebar ? 3 : 2}
        >
          {isToggleSidebar && (
            <Text flex={1} color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
              {t('translator.jobs')}
            </Text>
          )}
          <Tooltip label={t('common.addNew')} placement={isToggleSidebar ? 'bottom' : 'right'}>
            <IconButton
              aria-label={t('common.addNew')}
              icon={<Plus size={isToggleSidebar ? 18 : 19} />}
              isDisabled={isBlockingAddNewTask}
              onClick={onAddNew}
              size='ds-md'
              variant='ds-ghost'
            />
          </Tooltip>
          {/* Single collapse affordance — lives in the header (desktop only) */}
          {!isInDrawer && (
            <Tooltip
              label={isToggleSidebar ? t('common.close') : t('common.open')}
              placement={isToggleSidebar ? 'bottom' : 'right'}
            >
              <IconButton
                aria-label={isToggleSidebar ? t('common.close') : t('common.open')}
                icon={isToggleSidebar ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
                onClick={onClickToggleSideBar}
                size='ds-md'
                variant='ds-ghost'
              />
            </Tooltip>
          )}
        </Flex>

        {/* List */}
        <Box
          flex={1}
          overflowY={isToggleSidebar ? 'auto' : 'hidden'}
          px={isToggleSidebar ? 3 : 0}
          pb={3}
          visibility={isToggleSidebar ? 'visible' : 'hidden'}
        >
          <Flex direction='column' gap='2px'>
            {data?.map((item) => {
              const isActive = selectedTaskId === item?.id;
              return (
                <Flex
                  key={item?.id}
                  align='flex-start'
                  gap={3}
                  px={3}
                  py={3}
                  bg={isActive ? 'primary.soft' : 'transparent'}
                  borderRadius='md'
                  _hover={{ bg: isActive ? 'primary.soft' : 'bg.canvas' }}
                  cursor='pointer'
                  transition='background .12s ease-out'
                  onClick={() => onChangeTask(item)}
                >
                  <Box flexShrink={0} mt='2px' color={isActive ? 'primary.solid' : 'text.subtle'}>
                    <Languages size={16} />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Tooltip label={item.name} placement='top-start'>
                      <Text
                        overflow='hidden'
                        color={isActive ? 'primary.solid' : 'text.default'}
                        fontSize='sm'
                        fontWeight={isActive ? 600 : 500}
                        whiteSpace='nowrap'
                        textOverflow='ellipsis'
                      >
                        {item?.name}
                      </Text>
                    </Tooltip>
                    <Text mt='2px' color='text.subtle' fontSize='xs'>
                      {timeFormat(item?.last_updated)}
                    </Text>
                  </Box>
                  <TaskOptions
                    data={item}
                    onClickDelete={onClickDelete}
                    onClickEdit={onClickEdit}
                  />
                </Flex>
              );
            })}
          </Flex>

          {current_page + 1 < total_page && (
            <Button
              w='100%'
              mt={2}
              fontWeight={500}
              onClick={onShowMore}
              size='ds-sm'
              variant='ds-ghost'
            >
              {t('common.moreHistory')}
            </Button>
          )}
        </Box>
      </Flex>
    </>
  );
};

interface TaskOptionsProps {
  data: TranslationTask;
  onClickDelete: (_data: TranslationTask) => void;
  onClickEdit: (_data: TranslationTask) => void;
}

const TaskOptions = ({ data, onClickDelete, onClickEdit }: TaskOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu placement='bottom-end'>
      <MenuButton
        as={IconButton}
        flexShrink={0}
        aria-label={t('common.edit')}
        icon={<MoreHorizontal size={16} />}
        onClick={(e) => e.stopPropagation()}
        size='ds-sm'
        variant='ds-ghost'
      />
      <MenuList minW='140px' bg='bg.surfaceRaised' borderColor='border.default' shadow='ds_lg'>
        {data?.type !== TEMPORARY && (
          <MenuItem
            color='text.default'
            fontSize='sm'
            bg='transparent'
            _hover={{ bg: 'primary.soft', color: 'primary.solid' }}
            onClick={(e) => {
              e.stopPropagation();
              onClickEdit(data);
            }}
          >
            {t('common.edit')}
          </MenuItem>
        )}
        <MenuItem
          color='state.error'
          fontSize='sm'
          bg='transparent'
          _hover={{ bg: 'state.errorSoft' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default TranslationJobs;
